// src/ReviewQueue.jsx
// ─────────────────────────────────────────────────────────────────────────
// Braeden's knowledge-review dashboard. Lives at /#review.
//
// What you see:
//   - 4 tabs: Needs Review / Pending / Promoted / Rejected
//   - Each candidate card: claim text + source(s) + composite score +
//     contradictions (side-by-side with conflicting existing claim) +
//     outcome stats if any teachings happened
//   - One-tap Promote / Reject / Edit
//   - Source reliability sub-page (leaderboard of trusted sources)
//   - Outcome stats sub-page (top-working + failing claims)
//
// Auth: admin token in localStorage `aria_admin_token` — same as the
// storage-agent dashboard.
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from 'react';

const apiBase = (typeof window !== 'undefined' && window.electronAPI) ? 'http://127.0.0.1:3001' : '';

function getAdminToken() {
  try { return localStorage.getItem('aria_admin_token') || ''; } catch (_) { return ''; }
}

async function api(method, path, body = null) {
  const headers = { 'Content-Type': 'application/json' };
  const t = getAdminToken();
  if (t) headers['Authorization'] = `Bearer ${t}`;
  const r = await fetch(`${apiBase}${path}`, {
    method, headers, body: body ? JSON.stringify(body) : undefined,
  });
  return r.json();
}

function fmtScore(n) { return n == null ? '—' : Math.round(n); }
function fmtPct(n)   { return n == null ? '—' : `${(n * 100).toFixed(0)}%`; }

// ── Top-level review page ────────────────────────────────────────────────
export default function ReviewQueue() {
  const [tab, setTab] = useState('needs_human_review');
  const [counts, setCounts] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trade, setTrade] = useState('');
  const [highStakesOnly, setHighStakesOnly] = useState(false);
  const [view, setView] = useState('queue'); // 'queue' | 'sources' | 'outcomes'
  const [tokenInput, setTokenInput] = useState(getAdminToken());
  const [authOk, setAuthOk] = useState(false);

  const loadCounts = useCallback(async () => {
    const r = await api('GET', '/api/aria/review/queue/counts');
    if (r?.ok) { setCounts(r); setAuthOk(true); }
    else setAuthOk(false);
  }, []);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams({ status: tab });
    if (trade) q.set('trade', trade);
    if (highStakesOnly) q.set('highStakes', '1');
    const r = await api('GET', `/api/aria/review/queue?${q.toString()}`);
    setLoading(false);
    if (r?.ok) setCandidates(r.candidates || []);
  }, [tab, trade, highStakesOnly]);

  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { if (authOk) loadQueue(); }, [tab, trade, highStakesOnly, authOk, loadQueue]);

  // Token gate
  if (!authOk) {
    return (
      <div className="review-page">
        <div className="review-page__header"><h1>Knowledge Review</h1></div>
        <div className="review-empty" style={{ maxWidth: 480, margin: '60px auto' }}>
          <div style={{ marginBottom: 14, fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
            Paste your admin token to access the review queue (the same token used
            for the storage-agent dashboard — set via <code>STORAGE_AGENT_ADMIN_TOKEN</code>
            in <code>HowTo-Backend/.env</code>).
          </div>
          <input
            type="password"
            className="review-input"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
          />
          <button
            className="review-btn review-btn--primary"
            onClick={() => { try { localStorage.setItem('aria_admin_token', tokenInput); } catch (_) {} loadCounts(); }}
            style={{ marginLeft: 10 }}
          >Authorize</button>
        </div>
      </div>
    );
  }

  return (
    <div className="review-page">
      <header className="review-page__header">
        <h1>Knowledge Review</h1>
        <nav className="review-subnav">
          <button className={`review-subnav__btn ${view === 'queue' ? 'active' : ''}`} onClick={() => setView('queue')}>Queue</button>
          <button className={`review-subnav__btn ${view === 'sources' ? 'active' : ''}`} onClick={() => setView('sources')}>Sources</button>
          <button className={`review-subnav__btn ${view === 'outcomes' ? 'active' : ''}`} onClick={() => setView('outcomes')}>Outcomes</button>
        </nav>
      </header>

      {view === 'queue' && (
        <>
          <div className="review-tabs">
            {[
              { k: 'needs_human_review', label: 'Needs review', n: counts.needs_human_review || 0, urgent: true },
              { k: 'pending',            label: 'Pending gates', n: counts.pending || 0 },
              { k: 'promoted',           label: 'Promoted',     n: counts.promoted || 0 },
              { k: 'rejected',           label: 'Rejected',     n: counts.rejected || 0 },
            ].map((t) => (
              <button
                key={t.k}
                className={`review-tab ${tab === t.k ? 'active' : ''} ${t.urgent && t.n > 0 ? 'urgent' : ''}`}
                onClick={() => setTab(t.k)}
              >{t.label} <span className="review-tab__count">{t.n}</span></button>
            ))}
          </div>

          <div className="review-filters">
            <input
              className="review-input"
              placeholder="Filter by trade (e.g. hvac, plumbing)"
              value={trade}
              onChange={(e) => setTrade(e.target.value)}
            />
            <label className="review-toggle">
              <input type="checkbox" checked={highStakesOnly} onChange={(e) => setHighStakesOnly(e.target.checked)} />
              <span>High-stakes only ({counts.high_stakes_open || 0} open)</span>
            </label>
            <button className="review-btn" onClick={async () => {
              const r = await api('POST', '/api/aria/review/run-gates', { limit: 100 });
              alert(`Gates run — promoted: ${r.promoted}, rejected: ${r.rejected}, sent to review: ${r.sent_to_review}`);
              loadCounts(); loadQueue();
            }}>Run gates →</button>
          </div>

          <div className="review-list">
            {loading && <div className="review-empty">Loading…</div>}
            {!loading && candidates.length === 0 && (
              <div className="review-empty">Nothing here. Try a different tab or filter.</div>
            )}
            {!loading && candidates.map((c) => (
              <CandidateCard
                key={c.id}
                candidate={c}
                onChanged={() => { loadCounts(); loadQueue(); }}
              />
            ))}
          </div>
        </>
      )}

      {view === 'sources' && <SourcesView />}
      {view === 'outcomes' && <OutcomesView />}
    </div>
  );
}

// ── Candidate card ───────────────────────────────────────────────────────
function CandidateCard({ candidate, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(null); // { claim, trade, region } when editing
  const [busy, setBusy] = useState(false);

  const supporters = JSON.parse(candidate.supporters || '[]');
  const contradictions = JSON.parse(candidate.contradictions || '[]');
  const score = candidate.composite_source_score;
  const isHighStakes = !!candidate.high_stakes;

  const expand = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (!detail) {
      const r = await api('GET', `/api/aria/review/candidate/${candidate.id}`);
      if (r?.ok) setDetail(r);
    }
  };

  const promote = async () => {
    if (busy) return; setBusy(true);
    await api('POST', `/api/aria/review/${candidate.id}/promote`, { reviewedBy: 'admin' });
    setBusy(false); onChanged?.();
  };
  const reject = async () => {
    if (busy) return; setBusy(true);
    await api('POST', `/api/aria/review/${candidate.id}/reject`, { reviewedBy: 'admin' });
    setBusy(false); onChanged?.();
  };
  const saveEdit = async (alsoDecide) => {
    if (busy) return; setBusy(true);
    await api('POST', `/api/aria/review/${candidate.id}/edit`, {
      ...edit, decision: alsoDecide || 'reviewing', reviewedBy: 'admin',
    });
    setBusy(false); setEdit(null); onChanged?.();
  };

  return (
    <div className={`review-card ${isHighStakes ? 'review-card--high-stakes' : ''}`}>
      <div className="review-card__top">
        <div className="review-card__claim" onClick={expand} role="button">
          {edit ? (
            <textarea
              className="review-input review-input--full"
              value={edit.claim}
              onChange={(e) => setEdit({ ...edit, claim: e.target.value })}
              rows={2}
            />
          ) : (candidate.claim)}
        </div>
        <div className="review-card__meta">
          {isHighStakes && <span className="review-pill review-pill--alert">⚠ {candidate.stakes_category}</span>}
          {candidate.trade && <span className="review-pill">{candidate.trade}</span>}
          {candidate.claim_type && <span className="review-pill review-pill--ghost">{candidate.claim_type}</span>}
          <span className="review-pill review-pill--score">score {fmtScore(score)}</span>
          <span className="review-pill review-pill--ghost">{supporters.length} src</span>
          {contradictions.length > 0 && <span className="review-pill review-pill--alert">⚡ {contradictions.length} conflict</span>}
        </div>
      </div>

      {expanded && (
        <div className="review-card__detail">
          <div className="review-card__sources">
            <h4>Supporting sources</h4>
            {detail?.supportingSources?.map((s) => (
              <div key={s.source_key} className="review-source-row">
                <span className="review-source-row__key">{s.source_key}</span>
                <span className="review-source-row__type">{s.source_type}</span>
                <span className="review-source-row__score">tier {fmtScore(s.tier_score)} · rel {fmtScore(s.reliability_score)}</span>
                <span className="review-source-row__rate">
                  {s.outcomes_validated > 0 || s.outcomes_failed > 0
                    ? `${s.outcomes_validated}✓/${s.outcomes_failed}✗`
                    : 'no outcomes yet'}
                </span>
              </div>
            ))}
          </div>

          {detail?.contradictions?.length > 0 && (
            <div className="review-card__contradictions">
              <h4>Contradictions with existing knowledge</h4>
              {detail.contradictions.map((cf, i) => (
                <div key={i} className="review-conflict">
                  <div className="review-conflict__existing">📚 {cf.existing_text}</div>
                  <div className="review-conflict__verdict">
                    <span>{cf.verdict}</span>
                    <span>conf {fmtPct(cf.confidence)}</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>{cf.reason}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {detail?.outcomes && detail.outcomes.total_teachings > 0 && (
            <div className="review-card__outcomes">
              <h4>Real-world outcomes</h4>
              <div className="review-outcomes-row">
                <span>Taught {detail.outcomes.total_teachings}×</span>
                <span>Captured {detail.outcomes.outcomes_captured}</span>
                <span>Success {fmtPct(detail.outcomes.success_rate)}</span>
              </div>
              <div className="review-outcomes-detail">
                {Object.entries(detail.outcomes.by_outcome).map(([k, v]) => (
                  <span key={k} className="review-pill review-pill--ghost">{k}: {v}</span>
                ))}
              </div>
            </div>
          )}

          {edit && (
            <div className="review-card__edit-extra">
              <div className="review-edit-row">
                <label>Trade <input className="review-input" value={edit.trade || ''} onChange={(e) => setEdit({ ...edit, trade: e.target.value })} /></label>
                <label>Sub-topic <input className="review-input" value={edit.sub_topic || ''} onChange={(e) => setEdit({ ...edit, sub_topic: e.target.value })} /></label>
                <label>Region <input className="review-input" value={edit.region || ''} onChange={(e) => setEdit({ ...edit, region: e.target.value })} /></label>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="review-card__actions">
        {!edit && (
          <>
            <button className="review-btn review-btn--primary" onClick={promote} disabled={busy}>Promote ✓</button>
            <button className="review-btn review-btn--reject" onClick={reject} disabled={busy}>Reject ✕</button>
            <button className="review-btn" onClick={() => setEdit({
              claim: candidate.claim, trade: candidate.trade,
              sub_topic: candidate.sub_topic, region: candidate.region,
            })}>Edit</button>
          </>
        )}
        {edit && (
          <>
            <button className="review-btn review-btn--primary" onClick={() => saveEdit('promoted')} disabled={busy}>Save + Promote</button>
            <button className="review-btn" onClick={() => saveEdit('reviewing')} disabled={busy}>Save (keep in review)</button>
            <button className="review-btn review-btn--reject" onClick={() => setEdit(null)}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Sources reliability view ─────────────────────────────────────────────
function SourcesView() {
  const [sources, setSources] = useState([]);
  const [worst, setWorst] = useState([]);
  useEffect(() => {
    api('GET', '/api/aria/review/sources?limit=30').then((r) => r?.ok && setSources(r.sources));
    api('GET', '/api/aria/review/sources/worst?limit=15').then((r) => r?.ok && setWorst(r.sources));
  }, []);
  return (
    <div className="review-sources">
      <h2>Top-reliability sources</h2>
      <table className="review-table">
        <thead><tr><th>Source</th><th>Type</th><th>Tier</th><th>Reliability</th><th>Composite</th><th>✓</th><th>✗</th></tr></thead>
        <tbody>
          {sources.map((s) => (
            <tr key={s.source_key}>
              <td>{s.source_key}</td>
              <td><span className="review-pill review-pill--ghost">{s.source_type}</span></td>
              <td>{fmtScore(s.tier_score)}</td>
              <td>{fmtScore(s.reliability_score)}</td>
              <td><strong>{fmtScore(s.composite_score)}</strong></td>
              <td style={{ color: 'var(--accent)' }}>{s.outcomes_validated}</td>
              <td style={{ color: '#ff8888' }}>{s.outcomes_failed}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h2 style={{ marginTop: 30 }}>Sources flagged for failures</h2>
      <table className="review-table">
        <thead><tr><th>Source</th><th>Type</th><th>Reliability</th><th>Rejected</th><th>Failed</th></tr></thead>
        <tbody>
          {worst.map((s) => (
            <tr key={s.source_key}>
              <td>{s.source_key}</td>
              <td><span className="review-pill review-pill--ghost">{s.source_type}</span></td>
              <td>{fmtScore(s.reliability_score)}</td>
              <td style={{ color: '#ff8888' }}>{s.claims_rejected}</td>
              <td style={{ color: '#ff8888' }}>{s.outcomes_failed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Outcomes view ────────────────────────────────────────────────────────
function OutcomesView() {
  const [top, setTop] = useState([]);
  const [failing, setFailing] = useState([]);
  useEffect(() => {
    api('GET', '/api/aria/review/outcomes/top?limit=15').then((r) => r?.ok && setTop(r.claims));
    api('GET', '/api/aria/review/outcomes/failing?limit=15').then((r) => r?.ok && setFailing(r.claims));
  }, []);
  return (
    <div className="review-outcomes">
      <h2>What's working — top success rates</h2>
      <ul className="review-outcomes-list">
        {top.map((c) => (
          <li key={c.claimId}>
            <span className="review-outcomes-list__rate" style={{ color: 'var(--accent)' }}>{fmtPct(c.success_rate)}</span>
            <span className="review-outcomes-list__claim">claim #{c.claimId}</span>
            <span className="review-pill review-pill--ghost">{c.total_teachings} taught</span>
            <span className="review-pill review-pill--ghost">{c.outcomes_captured} measured</span>
          </li>
        ))}
      </ul>
      <h2 style={{ marginTop: 30 }}>What's failing — review urgently</h2>
      <ul className="review-outcomes-list">
        {failing.map((c) => (
          <li key={c.claimId}>
            <span className="review-outcomes-list__rate" style={{ color: '#ff8888' }}>{fmtPct(c.success_rate)}</span>
            <span className="review-outcomes-list__claim">claim #{c.claimId}</span>
            <span className="review-pill review-pill--ghost">{c.total_teachings} taught</span>
            <span className="review-pill review-pill--ghost">{c.outcomes_captured} measured</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
